import { api } from "@/redux/api";

import { TrackerEntityKind, TrackerImportPriorityPreset } from "@/enum/tracker.enum";

import {
	TrackerListImport,
	TrackerListImportListSchema,
	TrackerListImportSchema,
} from "../../pages/ProfileTracker/schema/profileTracker";

/** Body of a new "import from a list" tracker source (PREC-3417). */
export interface CreateTrackerListImportBody {
	listId: number;
	entityKind: TrackerEntityKind;
	priorityPreset: TrackerImportPriorityPreset;
	isLive?: boolean;
}

/**
 * Endpoints behind the reworked Sources step of a Profile Tracker (PREC-3417): the rows a
 * source had to skip, and the "import from a list" sources.
 *
 * Their own slice rather than more surface on `profileTracker.api.ts`, which the 500-line gate
 * no longer lets grow.
 */
export const trackerSourcesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * The rows a source could not track, as an xlsx to fix and re-upload (PREC-3417).
		 * A `query` rather than a mutation so it can be fired lazily from the source card.
		 */
		downloadSkippedTrackerRows: builder.query<Blob, number>({
			query: (trackerFileId) => ({
				url: `tracker-file/${trackerFileId}/skipped-rows`,
				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		getTrackerListImports: builder.query<TrackerListImport[], number>({
			query: (listId) => ({
				url: `tracker-list-import/list/${listId}`,
				responseSchema: TrackerListImportListSchema,
			}),
			providesTags: ["TrackerListImport"],
		}),
		createTrackerListImport: builder.mutation<
			TrackerListImport,
			{ listId: number; body: CreateTrackerListImportBody }
		>({
			query: ({ listId, body }) => ({
				url: `tracker-list-import/list/${listId}`,
				method: "POST",
				body,
				responseSchema: TrackerListImportSchema,
			}),
			// The import runs immediately, so the list itself has new leads too.
			invalidatesTags: (result, error, arg) => [
				"TrackerListImport",
				{ type: "List", id: arg.listId },
			],
		}),
		updateTrackerListImport: builder.mutation<
			TrackerListImport,
			{ id: number; isLive?: boolean; isActive?: boolean }
		>({
			query: ({ id, ...body }) => ({
				url: `tracker-list-import/${id}`,
				method: "PATCH",
				body,
				responseSchema: TrackerListImportSchema,
			}),
			invalidatesTags: ["TrackerListImport"],
		}),
		deleteTrackerListImport: builder.mutation<{ id: number }, number>({
			query: (id) => ({
				url: `tracker-list-import/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["TrackerListImport"],
		}),
	}),
});

export const {
	useLazyDownloadSkippedTrackerRowsQuery,
	useGetTrackerListImportsQuery,
	useCreateTrackerListImportMutation,
	useUpdateTrackerListImportMutation,
	useDeleteTrackerListImportMutation,
} = trackerSourcesApi;
